import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

const TICK_INTERVAL_MS = 1000; // one-second display cadence

// Module scope, not per-hook: one interval serves every subscriber, so a dispatch board
// with a hundred live timers still runs a single timer. Ref-counted through the subscriber
// set — the interval starts with the first subscriber and stops with the last.
const subscribers = new Set<() => void>();
let intervalId: ReturnType<typeof setInterval> | null = null;

function notifySubscribers() {
  subscribers.forEach((onTick) => onTick());
}

function subscribeToTicker(onTick: () => void) {
  subscribers.add(onTick);

  if (intervalId === null) {
    intervalId = setInterval(notifySubscribers, TICK_INTERVAL_MS);
  }
}

function unsubscribeFromTicker(onTick: () => void) {
  subscribers.delete(onTick);

  if (subscribers.size === 0 && intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

/**
 * The current time, re-rendering the caller once per second while `isEnabled`. Pass
 * `false` whenever the caller has nothing ticking — it then never re-renders on its own.
 *
 * The returned value is a fresh `Dayjs` every render: use it for display math, never as a
 * `useEffect` / `useMemo` dependency.
 */
export default function useCurrentTime(isEnabled: boolean) {
  const [, setTickCount] = useState(0);

  useEffect(() => {
    if (!isEnabled) return;

    const onTick = () => setTickCount((count) => count + 1);
    subscribeToTicker(onTick);

    return () => unsubscribeFromTicker(onTick);
  }, [isEnabled]);

  return dayjs();
}
